import {withScope, captureMessage} from '@sentry/vue';
import {snackbar} from '@teemill/common/services';
import {tracker} from '@teemill/common/plugins';
import {formatUrl} from '@teemill/common/helpers';

export default class Stripe {
  constructor(stripe) {
    this.stripe = stripe;
    this.orderHash = null;
    this.recaptchaRequired = false;
  }
  /**
   * Reference: https://stripe.com/docs/api/errors
   * Reference: https://stripe.com/docs/error-codes
   */
  handleFailedResponse(error) {
    tracker.event('payment-failed');
    const allowedErrors = {
      serverTypes: [
        'validation_error',
        'card_error',
        'api_connection_error',
        'error',
      ],
      clientCodes: [
        'incorrect_number',
        'card_number',
        'payment_intent_authentication_failure',
        'card_declined',
      ],
    };

    // Server-side error
    if (error && allowedErrors.serverTypes.includes(error.type)) {
      snackbar.error(error.message);

      return;
    }

    // Client-side error
    if (error && error.type === 'invalid_request_error') {
      if (allowedErrors.clientCodes.includes(error.code)) {
        snackbar.error(error.message);

        return;
      }
    }

    withScope(scope => {
      scope.setExtra('billingDetails', this.billingDetails);
      scope.setExtra('error', error);
      captureMessage(error.message);
    });

    snackbar.error(
      'Oops... looks like something went wrong. Please try again.'
    );
  }

  async createPaymentMethod(stripe, billingDetails) {
    const formattedBillingDetails = this.formatBillingDetails(billingDetails);
    const {paymentMethod, error} = await this.stripe.createPaymentMethod(
      'card',
      stripe,
      {
        billing_details: formattedBillingDetails,
      }
    );

    if (error) {
      this.handleFailedResponse(error);
      return {
        failedToCreate: true,
        error,
      };
    }

    return paymentMethod;
  }

  async createPaymentMethodFromElements(elements, billingDetails) {
    const formattedBillingDetails = this.formatBillingDetails(billingDetails);
    const {paymentMethod, error} = await this.stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: formattedBillingDetails,
      },
    });

    if (error) {
      this.handleFailedResponse(error);
      return {
        failedToCreate: true,
        error,
      };
    }

    return paymentMethod;
  }


  async handlePay(payRoute, scaRoute, postData) {
    if (typeof postData === 'object') {
      await this.pay(payRoute, postData);

      if (this.recaptchaRequired) {
        return false;
      }

      if (this.stripePayResponse.requires_action) {
        return this.scaPay(scaRoute, postData);
      } else {
        return {
          orderHash: this.stripePayResponse,
        };
      }
    }

    return false;
  }

  async scaPay(scaRoute, postData) {
    const {error: errorAction, paymentIntent} =
      await this.stripe.handleNextAction({
        clientSecret: this.stripePayResponse.payment_intent_client_secret,
      });

    if (errorAction) {
      this.handleFailedResponse(errorAction);
      return false;
    } else if (paymentIntent) {
      delete postData.stripe_payment_method;
      postData = Object.assign(postData, {
        stripe_payment_intent: paymentIntent.id,
      });

      await this.pay(scaRoute, postData);

      if (!this.stripePayResponse.requires_action) {
        return {
          orderHash: this.stripePayResponse,
        };
      } else {
        return this.scaPay(scaRoute, postData);
      }
    }
  }

  async pay(payRoute, postData) {
    await $axios
      .post(formatUrl(payRoute), postData)
      .success(data => {
        this.stripePayResponse = data;
      })
      .validation()
      .handle(403, () => {
        this.recaptchaRequired = true;
      })
      .handle(409, error => {
        snackbar.error(error.message);

        window.$router.push('/basket/');
      })
      .handle(423, error => {
        snackbar.error(error.message);

        setTimeout(() => {
          location.reload();
        }, 500);
      })
      .handle([400, 402], error => this.handleFailedResponse(error))
      .oops(
        'Unable to process your card payment at this time, try using PayPal.'
      );
  }

  formatBillingDetails(details) {
    return {
      address: {
        city: details.city,
        country: details.country,
        line1: details.street_address,
        line2: null,
        postal_code: details.zip,
        state: details.state,
      },
      email: details.email,
      name: details.first_name.concat(' ', details.last_name),
      phone: details.phone || '',
    };
  }
}
